import { useEffect, useState, type FormEvent } from "react";
import type { AuthMethods, User } from "@quill/shared";
import { ApiError, NetworkError, api } from "../api";

/** Messages for the `?sso_error=` the server redirects back with (SsoError on the server). */
const ssoErrors: Record<string, string> = {
  unavailable: "Single sign-on isn't reachable right now. Try again in a moment.",
  expired: "That sign-in took too long or was already used. Please try again.",
  denied: "Single sign-on didn't allow this sign-in.",
  failed: "Single sign-on failed. Please try again.",
  no_account: "There's no Quill account for that login.",
};
const NETWORK_ERROR = "Can't reach the server. Check your connection.";

/** Reads the single sign-on error once and removes it from the address bar. */
function takeSsoError(): string | null {
  const params = new URLSearchParams(location.search);
  const code = params.get("sso_error");
  if (code === null) return null;
  params.delete("sso_error");
  const query = params.toString();
  history.replaceState(null, "", location.pathname + (query ? `?${query}` : ""));
  return ssoErrors[code] ?? ssoErrors.failed!;
}

export function Login({ onLogin }: { onLogin(user: User): void }) {
  const [methods, setMethods] = useState<AuthMethods | null>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(takeSsoError);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .authMethods()
      .then(setMethods)
      .catch(() => setError(NETWORK_ERROR));
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onLogin(await api.login(username.trim().toLowerCase(), password));
    } catch (err) {
      setError(
        err instanceof NetworkError
          ? NETWORK_ERROR
          : err instanceof ApiError && err.status === 429
            ? "Too many attempts. Wait a minute and try again."
            : "That username and password don't match.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <div className="login-box">
        <h1>Quill</h1>
        {methods?.sso && (
          // A full-page navigation: the server redirects to the provider and back.
          <a className="sso" href="/api/auth/oidc/start">
            Sign in with single sign-on
          </a>
        )}
        {methods?.sso && methods.password && <p className="or">or</p>}
        {methods?.password && (
          <form onSubmit={submit}>
            <label>
              Username
              <input
                name="username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </label>
            <label>
              Password
              <input
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            <button type="submit" disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
      </div>
    </main>
  );
}
